import { format } from "date-fns";

import { EmailSuppressionReason, EmailSuppressionRow } from "@signalist/shared-types";

import { TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";

/** Translates a key with interpolation values, as i18next's `t` does. */
export type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * The label key of each suppression reason (PREC-4266). A closed map over the reasons the API can
 * answer, so a reason is never shown as its program value.
 */
export const SUPPRESSION_REASON_LABEL_KEYS: Record<EmailSuppressionReason, string> = {
	HARD_BOUNCE: "contacts.suppressed.reasons.HARD_BOUNCE",
	PROVIDER_BLOCK: "contacts.suppressed.reasons.PROVIDER_BLOCK",
};

/** "Hard bounce · 5.1.1", or "Hard bounce" while the SMTP code is unknown. */
export function suppressionReasonText(
	row: Pick<EmailSuppressionRow, "reason" | "smtpCode">,
	t: Translate
): string {
	const reason = t(SUPPRESSION_REASON_LABEL_KEYS[row.reason]);
	const code = row.smtpCode?.trim();

	return code ? t("contacts.suppressed.reasonWithCode", { reason, code }) : reason;
}

/** SUPPRESSED ON in the user's local time, as Added to Blacklist shows it: `HH:mm dd.MM.yyyy`. */
export function formatSuppressedOn(suppressedAt: string): string {
	return format(new Date(suppressedAt), "HH:mm dd.MM.yyyy");
}

/**
 * The page to show once a removal succeeded: the previous page when it took the last row of a
 * page after the first, so the table does not land on an empty page.
 */
export function pageAfterRemoval(page: number, rowsOnPage: number): number {
	return rowsOnPage <= 1 && page > 1 ? page - 1 : page;
}

/** The Select-columns layout of a set of columns, for a table with no saved layout yet. */
export function columnsToTableConfig(columns: TableColumn[]): TItem[] {
	return columns.map((column, index) => ({
		uid: column.uid ?? "",
		id: index + 1,
		show: column.show ?? true,
		content: column.title,
		locked: !!column.locked,
		allowExclude: column.allowExclude ?? true,
		custom: false,
		moveable: column.moveable,
		hideable: column.hideable,
	}));
}
